import { AspectRatio, Box, chakra, ChakraProps, Flex, Skeleton } from '@chakra-ui/react';
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Icon, IconBox } from '../Icon/Icon';
import Lazy from '../Lazy/Lazy';

const VideoElement = chakra.video;

const sx: ChakraProps['sx'] = {
  'video ~ div > *': {
    opacity: 0.8,
    transition: 'opacity .1s',
  },
  'video ~ div:hover > div': {
    opacity: 0.9,
  },
  '.playing ~ div > div': {
    opacity: 0,
  },
  '.playing ~ div:hover > div': {
    opacity: 0.5,
  },
  'video ~ div .icon': {
    display: 'none',
  },
  'video:not(.playing) ~ div .play-icon': {
    display: 'initial',
  },
  '.playing ~ div .pause-icon': {
    display: 'initial',
  },
};
const videoLoader = {
  fallback: (
    <AspectRatio ratio={16 / 9}>
      <Skeleton w="full" h="full" />
    </AspectRatio>
  ),
};

export interface VideoProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'color' | 'width' | 'height'>,
    ChakraProps {
  video: { src: string; type: 'video/webm' | 'video/mp4' | 'video/ogg' }[];
  lazy?: boolean;
}

const Video = ({
  video,
  lazy,
  autoPlay,
  muted,
  ...rest
}: VideoProps): React.ReactElement | null => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const onToggle = useCallback(
    (play?: boolean) => {
      const elem = videoRef.current;
      if (!elem) return;
      const next = play ?? !(elem.currentTime > 0 && !elem.paused && !elem.ended);
      elem.defaultMuted = !!muted;
      elem.muted = !!muted;
      if (next) {
        elem.load();
        elem.play();
        elem.classList.add('playing');
      } else {
        elem.pause();
        elem.classList.remove('playing');
      }
    },
    [muted],
  );

  const url = video?.[0]?.src;
  useEffect(() => {
    if (autoPlay && url) onToggle(true);
  }, [autoPlay, onToggle, url]);

  const Wrapper = lazy ? Lazy : Box;
  return (
    <Wrapper
      position="relative"
      w={rest.w}
      h={rest.h}
      sx={sx}
      {...(lazy ? videoLoader : undefined)}
    >
      <VideoElement
        ref={videoRef}
        preload="none"
        controls={false}
        w="100%"
        h="auto"
        autoPlay={autoPlay}
        muted={muted}
        outline="none!important"
        {...rest}
      >
        {video.map((vidProps) => (
          <source key={vidProps.type} {...vidProps} />
        ))}
      </VideoElement>
      {!autoPlay ? (
        <Flex
          w="full"
          h="full"
          position="absolute"
          top={0}
          left={0}
          align="center"
          justify="center"
          zIndex={2}
          onClick={() => onToggle()}
          cursor="button"
          role="button"
        >
          <IconBox size="lg" rounded="full" bg="white">
            <Icon className="icon play-icon" icon={faPlay} size="lg" color="gray.900" />
            <Icon className="icon pause-icon" icon={faPause} size="lg" color="gray.900" />
          </IconBox>
        </Flex>
      ) : null}
    </Wrapper>
  );
};
export default Video;
